import React, { createContext, useContext, useReducer } from 'react';

const SoftphoneContext = createContext();

// Action types
const SOFTPHONE_ACTIONS = {
  SET_STATUS: 'SET_STATUS',
  SET_REGISTERED: 'SET_REGISTERED',
  SET_IN_CALL: 'SET_IN_CALL',
  SET_CONNECTION_INFO: 'SET_CONNECTION_INFO',
  SET_FUNCTIONS: 'SET_FUNCTIONS',
  SET_MUTED: 'SET_MUTED',
  SET_CONFIG: 'SET_CONFIG',
  SET_AUDIO_LEVELS: 'SET_AUDIO_LEVELS',
  SET_CALL_DATA: 'SET_CALL_DATA',
  CLEAR_CALL_DATA: 'CLEAR_CALL_DATA',
  SET_LAST_SKILL_CONFIG: 'SET_LAST_SKILL_CONFIG',
  CLEAR_LAST_SKILL_CONFIG: 'CLEAR_LAST_SKILL_CONFIG',
  SET_TYPIFICATION_DATA: 'SET_TYPIFICATION_DATA',
  CLEAR_TYPIFICATION_DATA: 'CLEAR_TYPIFICATION_DATA',
  SET_HAS_ACTIVE_RTP: 'SET_HAS_ACTIVE_RTP', // Nuevo: para indicar RTP activo
  SET_MONITORING_SESSION: 'SET_MONITORING_SESSION',
  CLEAR_MONITORING_SESSION: 'CLEAR_MONITORING_SESSION',
  // Multi-call actions
  ADD_CALL: 'ADD_CALL',
  REMOVE_CALL: 'REMOVE_CALL',
  SET_CURRENT_CALL: 'SET_CURRENT_CALL',
  HOLD_CALL: 'HOLD_CALL',
  UNHOLD_CALL: 'UNHOLD_CALL',
  MERGE_CALLS: 'MERGE_CALLS',
  ADD_CONFERENCE_PARTICIPANT: 'ADD_CONFERENCE_PARTICIPANT',
  REMOVE_CONFERENCE_PARTICIPANT: 'REMOVE_CONFERENCE_PARTICIPANT',
  CLEAR_ALL_CALLS: 'CLEAR_ALL_CALLS',
  RESET: 'RESET'
};

// Initial state
const initialState = {
  status: 'Desconectado',
  registered: false,
  inCall: false,
  hasActiveRtp: false, // Nuevo: indica si hay transmisión RTP activa
  extension: null,
  domain: null,
  connected: false,
  // Softphone function references
  makeCall: null,
  hangup: null,
  toggleMute: null,
  holdCall: null,
  resumeCall: null,
  switchCall: null,
  mergeCall: null,
  hangupConferenceParticipant: null,
  muted: false,
  // Configuration
  config: {
    wsServer: '',
    domain: '',
    user: '',
    password: '',
    stun: 'stun:stun.l.google.com:19302',
    autoAnswer: true
  },
  // Audio levels
  audioLevels: {
    localLevel: 0,
    remoteLevel: 0
  },
  // Current call data
  callData: null,
  // Last skill config for typification
  lastSkillConfig: null,
  // PERSISTENT typification data - NEVER auto-cleared
  typificationData: null,
  // Supervisor monitoring session
  monitoringSession: null,
  // Multi-call state
  calls: {},               // { [callId]: { callData, state: 'active'|'held'|'conference' } }
  currentCallId: null,     // Currently active callId
  heldCallIds: [],         // Array of callIds on hold
  conferenceParticipants: [], // [{ callId, phoneNumber }] - merged conference participants
  // Configuration functions
  connect: null,
  disconnect: null,
  answer: null,
  reject: null
};

// Reducer
const softphoneReducer = (state, action) => {
  switch (action.type) {
    case SOFTPHONE_ACTIONS.SET_STATUS:
      return {
        ...state,
        status: action.payload,
        connected: action.payload !== 'Desconectado'
      };

    case SOFTPHONE_ACTIONS.SET_REGISTERED:
      return {
        ...state,
        registered: action.payload
      };

    case SOFTPHONE_ACTIONS.SET_IN_CALL:
      return {
        ...state,
        inCall: action.payload
      };

    case SOFTPHONE_ACTIONS.SET_HAS_ACTIVE_RTP:
      return {
        ...state,
        hasActiveRtp: action.payload
      };

    case SOFTPHONE_ACTIONS.SET_CONNECTION_INFO:
      return {
        ...state,
        extension: action.payload.extension,
        domain: action.payload.domain
      };

    case SOFTPHONE_ACTIONS.SET_FUNCTIONS:
      return {
        ...state,
        makeCall: action.payload.makeCall,
        hangup: action.payload.hangup,
        toggleMute: action.payload.toggleMute,
        connect: action.payload.connect,
        disconnect: action.payload.disconnect,
        answer: action.payload.answer,
        reject: action.payload.reject,
        holdCall: action.payload.holdCall || null,
        resumeCall: action.payload.resumeCall || null,
        switchCall: action.payload.switchCall || null,
        mergeCall: action.payload.mergeCall || null,
        hangupConferenceParticipant: action.payload.hangupConferenceParticipant || null
      };

    case SOFTPHONE_ACTIONS.SET_MUTED:
      return {
        ...state,
        muted: action.payload
      };

    case SOFTPHONE_ACTIONS.SET_CONFIG:
      return {
        ...state,
        config: { ...state.config, ...action.payload }
      };

    case SOFTPHONE_ACTIONS.SET_AUDIO_LEVELS:
      return {
        ...state,
        audioLevels: action.payload
      };

    case SOFTPHONE_ACTIONS.SET_CALL_DATA:
      return {
        ...state,
        callData: action.payload
      };

    case SOFTPHONE_ACTIONS.CLEAR_CALL_DATA:
      return {
        ...state,
        callData: null
      };

    case SOFTPHONE_ACTIONS.SET_LAST_SKILL_CONFIG:
      return {
        ...state,
        lastSkillConfig: action.payload
      };

    case SOFTPHONE_ACTIONS.CLEAR_LAST_SKILL_CONFIG:
      return {
        ...state,
        lastSkillConfig: null
      };

    case SOFTPHONE_ACTIONS.SET_TYPIFICATION_DATA:
      return {
        ...state,
        typificationData: action.payload
      };

    case SOFTPHONE_ACTIONS.CLEAR_TYPIFICATION_DATA:
      return {
        ...state,
        typificationData: null
      };

    case SOFTPHONE_ACTIONS.SET_MONITORING_SESSION:
      return {
        ...state,
        monitoringSession: action.payload
      };

    case SOFTPHONE_ACTIONS.CLEAR_MONITORING_SESSION:
      return {
        ...state,
        monitoringSession: null
      };

    // --- Multi-call reducers ---
    case SOFTPHONE_ACTIONS.ADD_CALL: {
      const { callId, callData } = action.payload;
      const newCalls = { ...state.calls, [callId]: { callData, state: 'active' } };
      return { ...state, calls: newCalls, currentCallId: callId, inCall: true };
    }

    case SOFTPHONE_ACTIONS.REMOVE_CALL: {
      const { [action.payload]: removed, ...restCalls } = state.calls;
      const remaining = Object.keys(restCalls);
      const newHeld = state.heldCallIds.filter(id => id !== action.payload);
      const newCurrentId = state.currentCallId === action.payload
        ? (remaining.length > 0 ? remaining[0] : null)
        : state.currentCallId;
      return {
        ...state,
        calls: restCalls,
        currentCallId: newCurrentId,
        heldCallIds: newHeld,
        inCall: remaining.length > 0,
        callData: newCurrentId ? (restCalls[newCurrentId]?.callData || null) : null,
        conferenceParticipants: remaining.length > 0 ? state.conferenceParticipants : []
      };
    }

    case SOFTPHONE_ACTIONS.SET_CURRENT_CALL:
      return { ...state, currentCallId: action.payload };

    case SOFTPHONE_ACTIONS.HOLD_CALL: {
      const hCallId = action.payload;
      const updCalls = { ...state.calls };
      if (updCalls[hCallId]) updCalls[hCallId] = { ...updCalls[hCallId], state: 'held' };
      const newHeldIds = state.heldCallIds.includes(hCallId)
        ? state.heldCallIds
        : [...state.heldCallIds, hCallId];
      const newCurrent = state.currentCallId === hCallId ? null : state.currentCallId;
      return { ...state, calls: updCalls, heldCallIds: newHeldIds, currentCallId: newCurrent };
    }

    case SOFTPHONE_ACTIONS.UNHOLD_CALL: {
      const uhCallId = action.payload;
      const updCalls2 = { ...state.calls };
      if (updCalls2[uhCallId]) updCalls2[uhCallId] = { ...updCalls2[uhCallId], state: 'active' };
      return {
        ...state,
        calls: updCalls2,
        heldCallIds: state.heldCallIds.filter(id => id !== uhCallId),
        currentCallId: uhCallId,
        callData: updCalls2[uhCallId]?.callData || state.callData
      };
    }

    case SOFTPHONE_ACTIONS.MERGE_CALLS: {
      const { primaryCallId, secondaryCallId } = action.payload;
      const { [secondaryCallId]: mergedOut, ...afterMerge } = state.calls;
      if (afterMerge[primaryCallId]) {
        afterMerge[primaryCallId] = { ...afterMerge[primaryCallId], state: 'conference' };
      }
      return {
        ...state,
        calls: afterMerge,
        heldCallIds: state.heldCallIds.filter(id => id !== secondaryCallId),
        currentCallId: primaryCallId,
        callData: afterMerge[primaryCallId]?.callData || state.callData
      };
    }

    case SOFTPHONE_ACTIONS.ADD_CONFERENCE_PARTICIPANT: {
      const { callId: pCallId, phoneNumber } = action.payload;
      const alreadyExists = state.conferenceParticipants.some(p => p.callId === pCallId);
      if (alreadyExists) return state;
      return {
        ...state,
        conferenceParticipants: [...state.conferenceParticipants, { callId: pCallId, phoneNumber }]
      };
    }

    case SOFTPHONE_ACTIONS.REMOVE_CONFERENCE_PARTICIPANT: {
      const removedId = action.payload;
      return {
        ...state,
        conferenceParticipants: state.conferenceParticipants.filter(p => p.callId !== removedId)
      };
    }

    case SOFTPHONE_ACTIONS.CLEAR_ALL_CALLS:
      return { ...state, calls: {}, currentCallId: null, heldCallIds: [], conferenceParticipants: [] };

    case SOFTPHONE_ACTIONS.RESET:
      return initialState;

    default:
      return state;
  }
};

// Provider component
export const SoftphoneProvider = ({ children }) => {
  const [state, dispatch] = useReducer(softphoneReducer, initialState);

  // Action creators
  const setStatus = (status) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_STATUS, payload: status });
  };

  const setRegistered = (registered) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_REGISTERED, payload: registered });
  };

  const setInCall = (inCall) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_IN_CALL, payload: inCall });
  };

  const setHasActiveRtp = (hasActiveRtp) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_HAS_ACTIVE_RTP, payload: hasActiveRtp });
  };

  const setConnectionInfo = (connectionInfo) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_CONNECTION_INFO, payload: connectionInfo });
  };

  const reset = () => {
    dispatch({ type: SOFTPHONE_ACTIONS.RESET });
  };

  const setSoftphoneFunctions = (functions) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_FUNCTIONS, payload: functions });
  };

  const setMuted = (muted) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_MUTED, payload: muted });
  };

  const setConfig = (config) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_CONFIG, payload: config });
  };

  const setAudioLevels = (levels) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_AUDIO_LEVELS, payload: levels });
  };

  const setCallData = (callData) => {
    const resolved = typeof callData === 'function' ? callData(state.callData) : callData;
    if (resolved === null) {
      console.trace('[SOFTPHONE_CONTEXT] setCallData called with NULL');
    } else {
      console.log('[SOFTPHONE_CONTEXT] setCallData called with:', resolved);
    }
    dispatch({ type: SOFTPHONE_ACTIONS.SET_CALL_DATA, payload: resolved });
  };

  const clearCallData = () => {
    console.log('[SOFTPHONE_CONTEXT] clearCallData called - clearing all call data');
    console.trace('[SOFTPHONE_CONTEXT] clearCallData stack trace');
    dispatch({ type: SOFTPHONE_ACTIONS.CLEAR_CALL_DATA });
  };

  const setLastSkillConfig = (skillConfig) => {
    console.log('[CONTEXT] Setting lastSkillConfig:', skillConfig);
    dispatch({ type: SOFTPHONE_ACTIONS.SET_LAST_SKILL_CONFIG, payload: skillConfig });
  };

  const clearLastSkillConfig = () => {
    console.log('[CONTEXT] Clearing lastSkillConfig');
    dispatch({ type: SOFTPHONE_ACTIONS.CLEAR_LAST_SKILL_CONFIG });
  };

  const setTypificationData = (data) => {
    console.log('[CONTEXT] Setting PERSISTENT typificationData:', data);
    dispatch({ type: SOFTPHONE_ACTIONS.SET_TYPIFICATION_DATA, payload: data });
  };

  const clearTypificationData = () => {
    console.log('[CONTEXT] Manually clearing typificationData');
    dispatch({ type: SOFTPHONE_ACTIONS.CLEAR_TYPIFICATION_DATA });
  };

  const setMonitoringSession = (session) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_MONITORING_SESSION, payload: session });
  };

  const clearMonitoringSession = () => {
    dispatch({ type: SOFTPHONE_ACTIONS.CLEAR_MONITORING_SESSION });
  };

  // Multi-call action creators
  const addCall = (callId, callData) => {
    dispatch({ type: SOFTPHONE_ACTIONS.ADD_CALL, payload: { callId, callData } });
  };

  const removeCall = (callId) => {
    dispatch({ type: SOFTPHONE_ACTIONS.REMOVE_CALL, payload: callId });
  };

  const setCurrentCall = (callId) => {
    dispatch({ type: SOFTPHONE_ACTIONS.SET_CURRENT_CALL, payload: callId });
  };

  const holdCallState = (callId) => {
    dispatch({ type: SOFTPHONE_ACTIONS.HOLD_CALL, payload: callId });
  };

  const unholdCallState = (callId) => {
    dispatch({ type: SOFTPHONE_ACTIONS.UNHOLD_CALL, payload: callId });
  };

  const mergeCallsState = (primaryCallId, secondaryCallId) => {
    dispatch({ type: SOFTPHONE_ACTIONS.MERGE_CALLS, payload: { primaryCallId, secondaryCallId } });
  };

  const addConferenceParticipant = (callId, phoneNumber) => {
    dispatch({ type: SOFTPHONE_ACTIONS.ADD_CONFERENCE_PARTICIPANT, payload: { callId, phoneNumber } });
  };

  const removeConferenceParticipant = (callId) => {
    dispatch({ type: SOFTPHONE_ACTIONS.REMOVE_CONFERENCE_PARTICIPANT, payload: callId });
  };

  const clearAllCalls = () => {
    dispatch({ type: SOFTPHONE_ACTIONS.CLEAR_ALL_CALLS });
  };

  const value = {
    ...state,
    setStatus,
    setRegistered,
    setInCall,
    setHasActiveRtp,
    setConnectionInfo,
    setSoftphoneFunctions,
    setMuted,
    setConfig,
    setAudioLevels,
    setCallData,
    clearCallData,
    setLastSkillConfig,
    clearLastSkillConfig,
    setTypificationData,
    clearTypificationData,
    setMonitoringSession,
    clearMonitoringSession,
    addCall,
    removeCall,
    setCurrentCall,
    holdCallState,
    unholdCallState,
    mergeCallsState,
    addConferenceParticipant,
    removeConferenceParticipant,
    clearAllCalls,
    reset
  };

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
    </SoftphoneContext.Provider>
  );
};

// Custom hook to use softphone context
export const useSoftphone = () => {
  const context = useContext(SoftphoneContext);
  if (!context) {
    throw new Error('useSoftphone must be used within a SoftphoneProvider');
  }
  return context;
};
